import { MasterUser, TheaterCompany, UpdateProductionInput, UpdateTheaterCompanyInput } from "@/graphql/types"
import { InputLabel, TextField } from "@mui/material"
import { useCallback, useMemo, useState } from "react"
import Button from "../common/button/button"
import * as GraphQLClient from "@apollo/client"
import { CreateProduction, CreateTheaterCompany, GetOwnTheaterCompanies, UpdateProduction, UpdateTheaterCompany } from "@/graphql/documentNode"
import { useSnackBar } from "@/lib/ts/hooks/useSnackBar"
import { useQuery } from "react-query"
import styled from "styled-components"
import DateTimePicker from "react-datetime-picker"
import dayjs from "dayjs"
import SelectItem from "../common/input/selectItem"
import SelectItemV2 from "../common/input/selectItemV2"

type Props = {
    theaterCompanies: TheaterCompany[]
    productionData?: UpdateProductionInput
    user: MasterUser
    onComplete: () => void
}

const dtFormat = "YYYY/MM/DD"

const CreateOrUpdateProduction = (props: Props) => {
    const snackBar = useSnackBar({})

    const [name, setName] = useState<string | undefined | null>("")
    const [description, setDescription] = useState<string | undefined | null>("")
    const [url, setUrl] = useState<string | undefined | null>("")
    const [startDate, setStartDate] = useState<number | undefined>(undefined)
    const [endDate, setEndDate] = useState<number | undefined>(undefined)
    const [location, setLocation] = useState<string | undefined | null>("")
    const [locationUrl, setLocationUrl] = useState<string | undefined | null>("")
    const [locationDescription, setLocationDescription] = useState<string | undefined | null>("")
    const [theaterCompanyId, setTheaterCompanyId] = useState<number | undefined>(undefined)



    const nameError = useMemo(() => {
        const texts: string[] = []
        if (!name) {
            texts.push("公演名は必須です")
        } else {
            if (name.length > 50) {
                texts.push("公演名は50文字以下です")
            }
        }
        return texts
    }, [name])

    const descriptionError = useMemo(() => {
        const texts: string[] = []
        if (description && description.length > 100) {
            texts.push("説明は100文字以下です")
        }
        return texts
    }, [description])

    const urlError = useMemo(() => {
        const texts: string[] = []
        if (url) {
            try {
                new URL(url)
            } catch (err) {
                texts.push("URLが無効です")
            }
        }
        return texts
    }, [url])

    const locationError = useMemo(() => {
        const texts: string[] = []
        if (!location) {
            texts.push("会場名は必須です")
        } else {
            if (location.length > 50) {
                texts.push("会場名は50文字以下です")
            }
        }
        return texts
    }, [location])

    const locationDescriptionError = useMemo(() => {
        const texts: string[] = []
        if (!locationDescription) {
            texts.push("会場説明は必須です")
        } else {
            if (locationDescription.length > 100) {
                texts.push("会場説明は100文字以下です")
            }
        }
        return texts
    }, [locationDescription])

    const locationUrlError = useMemo(() => {
        const texts: string[] = []
        if (!locationUrl) {
            texts.push("会場URLは必須です")
        } else {
            try {
                new URL(locationUrl)
            } catch (err) {
                texts.push("URLが無効です")
            }
        }
        return texts
    }, [locationUrl])


    useQuery([props], () => {
        if (props.productionData) {
            setName(props.productionData.name)
            setDescription(props.productionData.description)
            setUrl(props.productionData.url)
            setStartDate(props.productionData.startDate ?? undefined)
            setEndDate(props.productionData.endDate ?? undefined)
            setTheaterCompanyId(props.productionData.theaterCompanyId ?? undefined)
            setLocation(props.productionData.location)
            setLocationUrl(props.productionData.location_url)
            setLocationDescription(props.productionData.location_description)
        } else {
            setName("")
            setDescription("")
            setUrl("")
            setStartDate(undefined)
            setEndDate(undefined)
            setTheaterCompanyId(-1)
            setLocation("")
            setLocationUrl("")
            setLocationDescription("")
        }
    })

    const [createProduction] = GraphQLClient.useMutation(CreateProduction, {
        variables: {
            input: {
                name,
                description,
                url,
                startDate,
                endDate,
                location,
                location_url: locationUrl,
                location_description: locationDescription,
                theaterCompanyId,
            }
        },
        onCompleted: (data) => {
            snackBar.onMutationSuccess()
        },
        onError: (err) => {
            snackBar.onMutationError()
        },
    })

    const [updateProduction] = GraphQLClient.useMutation(UpdateProduction, {
        variables: {
            input: {
                id: props.productionData?.id,
                name,
                description,
                url,
                startDate,
                endDate,
                location,
                location_url: locationUrl,
                location_description: locationDescription,
                theaterCompanyId,
            }
        },
        onCompleted: (data) => {
            snackBar.onMutationSuccess()
        },
        onError: (err) => {
            snackBar.onMutationError()
        },
    })
    const disableSubmit = useMemo(() => {
        return !!nameError.length || !!descriptionError.length || !!urlError.length || !!locationError.length || !!locationDescriptionError.length || !!locationUrlError.length || theaterCompanyId === undefined
    }, [descriptionError.length, locationDescriptionError.length, locationError.length, locationUrlError.length, nameError.length, theaterCompanyId, urlError.length])

    const submit = useCallback(async () => {
        if (disableSubmit) {
            return
        }
        snackBar.loadStart()
        if (props.productionData) {
            await updateProduction()
        } else {
            await createProduction()
        }
        props.onComplete()
    }, [createProduction, disableSubmit, props, snackBar, updateProduction])

    const onTheaterCompanyChanged = useCallback((theaterCompany: TheaterCompany, _: number) => {
        setTheaterCompanyId(theaterCompany.id)
    }, [])
    const theaterCompanyIndex = useMemo(() => {
        if(!theaterCompanyId) {
            return -1
        }
        return props.theaterCompanies.findIndex(item => item.id === theaterCompanyId)
    }, [props.theaterCompanies, theaterCompanyId])


    return (
        <CreateOrUpdateProductionStyle>
            <div className="input-area">
                <div className="input">
                    <InputLabel>団体</InputLabel>
                    <SelectItemV2 
                        items={props.theaterCompanies}
                        selectedIndex={theaterCompanyIndex}
                        width={250}
                        changeIndex={onTheaterCompanyChanged}
                    />
                </div>
                <div className="input">
                    <InputLabel>公演名</InputLabel>
                    <TextField
                        defaultValue={props.productionData?.name}
                        key={props.productionData?.name}
                        sx={{ width: "250px" }}
                        variant="standard"
                        placeholder="劇団One-Ahead"
                        onChange={(event) => {
                            setName(event.target.value);
                        }}
                        error={!!nameError.length}
                        helperText={nameError}
                    />
                </div>
                <div className="input">
                    <InputLabel>説明</InputLabel>
                    <TextField
                        defaultValue={props.productionData?.description}
                        key={props.productionData?.description}
                        sx={{ width: "250px" }}
                        variant="standard"
                        placeholder="予約システムを運営しています"
                        onChange={(event) => {
                            setDescription(event.target.value);
                        }}
                        error={!!descriptionError.length}
                        helperText={descriptionError}
                    />
                </div>
                <div className="input">
                    <InputLabel>サイト等URL</InputLabel>
                    <TextField
                        defaultValue={props.productionData?.url}
                        key={props.productionData?.url}
                        sx={{ width: "250px" }}
                        variant="standard"
                        placeholder="https://one-ahead.com"
                        onChange={(event) => {
                            setUrl(event.target.value);
                        }}
                        error={!!urlError.length}
                        helperText={urlError}
                    />
                </div>
                <div className="input">
                    <InputLabel>開始日</InputLabel>
                    <DateTimePicker
                        defaultValue={props.productionData?.startDate ? dayjs.unix(props.productionData?.startDate).toDate() : undefined}
                        key={props.productionData?.startDate}
                        className="dateTimePicker"
                        disableClock
                        hourHandLength={2}
                        maxDate={endDate ? dayjs.unix(endDate).toDate() : undefined}
                        value={
                            startDate ? dayjs.unix(startDate).toDate() : undefined
                        }
                        onChange={(value) => {
                            setStartDate(dayjs(value).unix());
                        }}
                    />
                </div>
                <div className="input">
                    <InputLabel>終了日</InputLabel>
                    <DateTimePicker
                        defaultValue={props.productionData?.endDate ? dayjs.unix(props.productionData?.endDate).toDate() : undefined}
                        key={props.productionData?.endDate}
                        className="dateTimePicker"
                        disableClock
                        hourHandLength={2}
                        minDate={startDate ? dayjs.unix(startDate).toDate() : undefined}
                        value={
                            endDate ? dayjs.unix(endDate).toDate() : undefined
                        }
                        onChange={(value) => {
                            setEndDate(dayjs(value).unix());
                        }}
                    />
                </div>
                <div className="input location">
                    <div className="input">
                        <InputLabel>会場名</InputLabel>
                        <TextField
                            defaultValue={props.productionData?.location}
                            key={props.productionData?.location}
                            sx={{ width: "250px" }}
                            variant="standard"
                            placeholder="OneAhead文化センター"
                            onChange={(event) => {
                                setLocation(event.target.value);
                            }}
                            error={!!locationError.length}
                            helperText={locationError}
                        />
                    </div>
                    <div className="input">
                        <InputLabel>会場説明</InputLabel>
                        <TextField
                            defaultValue={props.productionData?.location_description}
                            key={props.productionData?.location_description}
                            sx={{ width: "250px" }}
                            variant="standard"
                            placeholder="A駅徒歩10分 / B駅徒歩5分"
                            onChange={(event) => {
                                setLocationDescription(event.target.value);
                            }}
                            error={!!locationDescriptionError.length}
                            helperText={locationDescriptionError}
                        />
                    </div>
                    <div className="input">
                        <InputLabel>会場URL</InputLabel>
                        <TextField
                            defaultValue={props.productionData?.location_url}
                            key={props.productionData?.location_url}
                            sx={{ width: "250px" }}
                            variant="standard"
                            placeholder=""
                            onChange={(event) => {
                                setLocationUrl(event.target.value);
                            }}
                            error={!!locationUrlError.length}
                            helperText={locationUrlError}
                        />
                    </div>
                </div>
            </div>
            <div>
                <Button
                    label="送信"
                    buttonType="powerful-red"
                    onClick={submit}
                    disabled={disableSubmit}
                />
            </div>
            {snackBar.component}
        </CreateOrUpdateProductionStyle>
    )
}

const CreateOrUpdateProductionStyle = styled.div`
    .input-area {
        .input {
            width: 250px;
            margin: auto;
            text-align: center;
        }
    }
`

export default CreateOrUpdateProduction